import { useEffect, useState } from 'react'
import { deleteSecret, fetchSecrets } from '../../api/ws'
import { useAppStore } from '../../store/app'
import type { Secret } from '../../store/types'
import { SecretValueForm } from './SecretValueForm'

// CRUD view for the caller's secrets. Scoped server-side by
// caller-owned hosts (no org filter). Create/rotate encrypts in the
// browser to the host's X25519 pubkey and uploads ciphertext only;
// the plaintext value never crosses the wire.
//
// Delete is a hard row delete and also cascade-deletes IAM rules
// referencing the secret (resource_kind='secret', resource_id=id).
// No undo.
//
// Reachable via gear → About → Debug → "Secrets".
export function SecretsSheet() {
  const secrets = useAppStore((s) => s.secrets)
  const hosts = useAppStore((s) => s.hosts)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [addHostID, setAddHostID] = useState<string>('')

  useEffect(() => {
    fetchSecrets()
  }, [])

  // Default the "add" host picker to the only host, when there is one.
  useEffect(() => {
    if (!addHostID && hosts.length === 1) setAddHostID(hosts[0].host_id)
  }, [hosts, addHostID])

  function handleError(e: unknown) {
    setError(typeof e === 'string' ? e : (e as Error)?.message ?? String(e))
  }

  // Group by host for readability. host_id may not match any known
  // host row (e.g. forgotten / revoked), so fall back to the raw id.
  const byHost = new Map<string, Secret[]>()
  for (const sec of secrets) {
    const list = byHost.get(sec.host_id) ?? []
    list.push(sec)
    byHost.set(sec.host_id, list)
  }

  const addHost = hosts.find((h) => h.host_id === addHostID) ?? null

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      <p style={{ opacity: 0.65, marginTop: 0 }}>
        Sensitive data like API keys and passwords that can be injected into agent commands without ever exposing them to the agents themselves.
      </p>

      {error && (
        <div
          style={{
            padding: '8px 12px',
            background: '#fee',
            color: '#900',
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        {!adding ? (
          <button onClick={() => setAdding(true)} disabled={hosts.length === 0}>
            + Add secret
          </button>
        ) : (
          <div
            style={{
              border: '1px solid #ddd',
              borderRadius: 4,
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, opacity: 0.7 }}>Host</span>
              <select
                value={addHostID}
                onChange={(e) => setAddHostID(e.target.value)}
                style={{ fontSize: 16 }}
              >
                <option value="">Select…</option>
                {hosts.map((h) => (
                  <option key={h.host_id} value={h.host_id}>
                    {h.hostname} ({h.host_id})
                  </option>
                ))}
              </select>
            </label>
            {addHost && (
              <SecretValueForm
                hostID={addHost.host_id}
                hostLabel={addHost.hostname}
                submitLabel="Create"
                onDone={() => {
                  setAdding(false)
                  fetchSecrets()
                }}
                onCancel={() => setAdding(false)}
              />
            )}
          </div>
        )}
      </div>

      {secrets.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No secrets on any of your hosts.</p>
      ) : (
        [...byHost.entries()].map(([hostID, rows]) => {
          const hostName = hosts.find((h) => h.host_id === hostID)?.hostname ?? hostID
          return (
            <div key={hostID} style={{ marginBottom: 20 }}>
              <h3
                style={{
                  margin: '4px 0',
                  fontSize: 13,
                  opacity: 0.7,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {hostName}
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {rows.map((sec) => (
                  <SecretRow
                    key={sec.id}
                    secret={sec}
                    hostName={hostName}
                    onError={handleError}
                    onClearError={() => setError(null)}
                    onMutated={fetchSecrets}
                  />
                ))}
              </ul>
            </div>
          )
        })
      )}
    </div>
  )
}

function SecretRow({
  secret,
  hostName,
  onError,
  onClearError,
  onMutated,
}: {
  secret: Secret
  hostName: string
  onError: (e: unknown) => void
  onClearError: () => void
  onMutated: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [rotating, setRotating] = useState(false)

  async function handleDelete() {
    if (
      !confirm(
        `Delete secret "${secret.name}"?\n\n` +
          'This removes the row and any IAM rules referencing it. ' +
          'Anything currently consuming this secret will start failing. ' +
          'No undo.',
      )
    ) {
      return
    }
    onClearError()
    setBusy(true)
    try {
      deleteSecret({ id: secret.id })
      // Server reply isn't typed for errors; re-list after a short beat.
      setTimeout(() => {
        onMutated()
        setBusy(false)
      }, 250)
    } catch (e) {
      onError(e)
      setBusy(false)
    }
  }

  const expired =
    secret.expires_at && new Date(secret.expires_at).getTime() < Date.now()

  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 8,
        }}
      >
        <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{secret.name}</div>
        {expired && (
          <div style={{ fontSize: 12, color: '#900', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Expired
          </div>
        )}
      </div>
      {secret.purpose && (
        <div style={{ opacity: 0.7, fontSize: 12 }}>{secret.purpose}</div>
      )}
      <div style={{ opacity: 0.6, fontSize: 12 }}>
        {secret.set_at && <>set {fmtDate(secret.set_at)} </>}
        {secret.set_by_kind && (
          <>by {secret.set_by_kind}
            {secret.set_by_id ? ` (${shortID(secret.set_by_id)})` : ''}{' '}
          </>
        )}
        {secret.expires_at && <>· expires {fmtDate(secret.expires_at)}</>}
      </div>
      <div style={{ opacity: 0.5, fontSize: 12, fontFamily: 'monospace' }}>{secret.id}</div>
      {rotating ? (
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: 4,
            padding: 12,
            marginTop: 4,
          }}
        >
          <SecretValueForm
            hostID={secret.host_id}
            hostLabel={hostName}
            nameLocked
            initialName={secret.name}
            submitLabel="Rotate"
            onDone={() => {
              setRotating(false)
              onMutated()
            }}
            onCancel={() => setRotating(false)}
          />
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setRotating(true)} disabled={busy}>
            Rotate value
          </button>
          <button onClick={handleDelete} disabled={busy} style={{ color: '#900' }}>
            Delete
          </button>
        </div>
      )}
    </li>
  )
}

function fmtDate(s: string): string {
  const d = new Date(s)
  if (isNaN(d.getTime())) return s
  return d.toLocaleString()
}

function shortID(id: string): string {
  return id.length > 8 ? id.slice(0, 8) + '…' : id
}
